import React, { useState, useEffect, useCallback } from 'react';

const ZoomIndicator = () => {
  const [zoomLevel, setZoomLevel] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hideTimeout, setHideTimeout] = useState(null);

  // Function to show indicator temporarily
  const showIndicator = useCallback(() => {
    setIsVisible(true);
    
    // Clear existing timeout
    setHideTimeout(prevTimeout => {
      if (prevTimeout) {
        clearTimeout(prevTimeout);
      }
      
      // Set new timeout to hide indicator
      return setTimeout(() => {
        setIsVisible(false);
      }, 2000); // Hide after 2 seconds
    });
  }, []);

  useEffect(() => {
    // Check if we're in Electron environment
    if (!window.require) return;

    try {
      const { webFrame, ipcRenderer } = window.require('electron');
      
      // Function to update zoom level
      const updateZoomLevel = (newZoom = null) => {
        const currentZoom = newZoom !== null ? newZoom : webFrame.getZoomLevel();
        console.log(`ZoomIndicator: Updating zoom level to ${currentZoom}`);
        setZoomLevel(currentZoom);
        showIndicator();
      };

      // Listen for zoom changes from main process
      const handleZoomChanged = (event, newZoom) => {
        console.log(`ZoomIndicator: Received zoom change event, new level: ${newZoom}`);
        updateZoomLevel(newZoom);
      };

      // Initial zoom level
      updateZoomLevel();

      // Add IPC listener for zoom changes
      ipcRenderer.on('zoom-changed', handleZoomChanged);

      // Fallback: Listen for zoom changes by checking periodically
      let lastKnownZoom = webFrame.getZoomLevel();
      const zoomCheckInterval = setInterval(() => {
        const currentZoom = webFrame.getZoomLevel();
        if (currentZoom !== lastKnownZoom) {
          lastKnownZoom = currentZoom;
          updateZoomLevel(currentZoom);
        }
      }, 500); // Check every 500ms (less frequent since we have IPC)

      // Cleanup
      return () => {
        clearInterval(zoomCheckInterval);
        ipcRenderer.removeListener('zoom-changed', handleZoomChanged);
        if (hideTimeout) {
          clearTimeout(hideTimeout);
        }
      };
    } catch (error) {
      console.log('ZoomIndicator: Not in Electron environment or webFrame not available');
    }
  }, []); // Empty dependency array to prevent infinite loop

  // Don't render if not in Electron or not visible
  if (!window.require || !isVisible) return null;

  // Calculate zoom percentage (Electron zoom levels: 0 = 100%, 1 = 110%, -1 = 90%, etc.)
  const zoomPercentage = Math.round(Math.pow(1.2, zoomLevel) * 100);
  
  // Check if at zoom limits (using same limits as main process)
  const MAX_ZOOM_LEVEL = 4.9;
  const MIN_ZOOM_LEVEL = -2.9;
  const isAtMaxZoom = zoomLevel >= MAX_ZOOM_LEVEL;
  const isAtMinZoom = zoomLevel <= MIN_ZOOM_LEVEL;
  const isAtLimit = isAtMaxZoom || isAtMinZoom;
  
  // Debug logging
  if (isAtLimit) {
    console.log(`ZoomIndicator: At limit - Level: ${zoomLevel}, Max: ${isAtMaxZoom}, Min: ${isAtMinZoom}, Percentage: ${zoomPercentage}%`);
  }

  return (
    <div className={`zoom-indicator ${isAtLimit ? 'at-limit' : ''} fixed top-4 right-4 z-50 bg-black bg-opacity-80 text-white px-4 py-2 rounded-lg shadow-xl border ${isAtLimit ? 'border-yellow-500' : 'border-gray-600'}`}>
      <div className="flex items-center space-x-2">
        <svg 
          className={`w-4 h-4 ${isAtLimit ? 'text-yellow-400' : 'text-blue-400'}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" 
          />
        </svg>
        <span className="text-sm font-semibold">
          {zoomPercentage}%
        </span>
        {isAtLimit && (
          <span className="text-xs text-yellow-400 ml-1 font-bold">
            {isAtMaxZoom ? 'MAX' : 'MIN'}
          </span>
        )}
      </div>
    </div>
  );
};

export default ZoomIndicator;